import { useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { capitalize, startCase, map, chain, keys, keyBy, find, toLower, pick, get } from "lodash"
import { Paper } from "@mui/material"
import Table from "components/material/table"
import { ROLE, PERMISSION, RESOURCE } from "helpers/shapes"
import { PERMISSION_LEVELS, PERMISSION_LEVELS_AS_OPTIONS } from "helpers/permissions"
import { getResourceLabel } from "helpers/permissions/resource-types"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"

export default function TokenPermissions({ account, role, tokenId, permissions, resources, update, datasetEntitlementsEnabled, tabs }) {
  const isAdmin = role === "admin"

  const getResource = useCallback((resourceId) => {
    return find(resources, ({ id }) => id === resourceId)
  }, [resources])
  const resourcesById = useMemo(() => keyBy(resources, "id"), [resources])

  const availableOptionLabels = useMemo(() => chain(resourcesById)
    .pickBy(({ id, permissionLevel }) => !find(permissions, p => p.id === id) && permissionLevel === "OWNER")
    .mapValues(getResourceLabel)
    .value(),
  [permissions, resourcesById])

  const formatResourceType = (resourceType) => toLower(resourceType) === "swap" ? "Basket" : startCase(toLower(resourceType))

  const addForm = useMemo(() => {
    if (!isAdmin) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($tokenId: Id!, $permissionLevel: PermissionLevel!) {
        ${map(data.resources, (resourceId, index) => `resource${index}: updatePermissions(
          resourceType: ${getResource(resourceId).type},
          resourceId: "${resourceId}",
          permissions: { tokenId: $tokenId, permissionLevel: $permissionLevel })
          { id }`)}
      }`

      const variables = { permissionLevel: data.permissionLevel, tokenId }

      update(query, variables, account, {
        reloadData: "tokenPermissions",
      }, cb)
    }

    return {
      title: "Add Token Permissions",
      submitText: "Add Permissions",
      track: {
        type: "permissioned_token_permission_added",
        payload: ({ resources, permissionLevel }) => ({
          resources: map(resources, id => resourcesById[id].name),
          permissionLevel,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "resources",
        label: "Resources",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
        sort: ([, label1], [, label2]) => label1.localeCompare(label2),
      }, {
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [isAdmin, availableOptionLabels, tokenId, update, account, getResource, resourcesById])

  const editForm = useMemo(() => {
    if (!isAdmin) { return null }

    function onEdit(data, cb) {
      const resourceId = get(data, "id")
      const resource = getResource(resourceId)
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $tokenId: Id!, $permissionLevel: PermissionLevel!) {
        updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { tokenId: $tokenId, permissionLevel: $permissionLevel }) { id }
      }`

      const variables = {
        resourceId,
        resourceType: resource.type,
        permissionLevel: data.permissionLevel,
        tokenId,
      }

      update(query, variables, account, {
        reloadData: "tokenPermissions",
      }, cb)
    }

    return {
      title: "Edit Token Permission",
      submitText: "Save",
      isEnabled: () => isAdmin,
      track: {
        type: "permissioned_token_permission_edited",
        payload: data => pick(data, "permissionLevel"),
      },
      onSubmit: onEdit,
      getInitialValues: ({ role }) => ({ role }),
      fields:
      [{
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [isAdmin, getResource, tokenId, update, account])

  const deleteForm = useMemo(() => {
    function onDelete(data, cb) {
      const query = `mutation ($tokenId: Id!) {
        ${map(data, (resource, index) => `resource${index}: updatePermissions(resourceType: ${resource.type}, resourceId: "${resource.id}", permissions: { tokenId: $tokenId, permissionLevel: NONE }) { id }`)}
      }`

      const variables = { tokenId }

      update(query, variables, account, {
        reloadData: "tokenPermissions",
      }, cb)
    }

    return {
      type: "permission",
      track: {
        type: "permissioned_token_permission_removed",
        payload: ({ name }) => ({ resource: name }),
      },
      isEnabled: () => isAdmin,
      renderItem: resource => <li key={resource.id}>{resource.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, isAdmin, tokenId, update])

  return <div className="modules_token">
    {datasetEntitlementsEnabled && <UpsellTabs id="token" tabs={tabs} />}
    <Paper className="content" square>
      <Table
        title="Token Permissions"
        idField="id"
        data={permissions}
        columns={[{
          name: "Name",
          id: "name",
        }, {
          name: "Type",
          id: "type",
          formatter: formatResourceType,
        }, {
          name: "Permission Level",
          id: "permissionLevel",
          formatter: capitalize,
        }]}
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
      />
    </Paper>
  </div>
}

TokenPermissions.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  tokenId: PropTypes.string.isRequired,
  permissions: PropTypes.arrayOf(PERMISSION),
  resources: PropTypes.arrayOf(RESOURCE).isRequired,
  update: PropTypes.func.isRequired,
  datasetEntitlementsEnabled: PropTypes.bool.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
